import { CdkDrag } from '@angular/cdk/drag-drop';
import { Component, computed, DestroyRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import {
  lucideArrowDown,
  lucideArrowUp,
  lucideEllipsisVertical,
  lucideFolderSearch,
  lucidePen,
  lucidePlus,
  lucideSearch,
  lucideTrash,
} from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { HlmTooltipTrigger } from '@spartan-ng/helm/tooltip';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';
import { CompétenceStore } from '../../../../bloc-competences/store/compétences.store';
import { Cartographie } from '../../../../cartographie/models/cartographie.model';
import { ChipComponent } from '../../../../shared/components/chip/chip.component';
import { DialogConfirmDeleteComponent } from '../../../../shared/components/dialog/confirm-delete/dialog-confirm-delete.component';
import { ConfirmDialogComponent } from '../../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { ExpansionPanelMenuAction } from '../../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { Phase } from '../../processus-phases/models/phase.model';
import { PhaseStore } from '../../processus-phases/store/phase.store';
import { FormAptitudeComponent } from '../form/form-aptitude/form-aptitude.component';
import { Aptitude } from '../models/aptitude.model';
import { AptitudeUpdateRequest } from '../requests/update-request';
import { AptitudeStore } from '../store/aptitude.store';

@Component({
  selector: 'app-item-aptitude-processus',
  imports: [
    ChipComponent,
    HlmButtonImports,
    HlmIconImports,
    CdkDrag,
    RouterModule,
    HlmDropdownMenuImports,
    HlmTooltipTrigger,
  ],
  templateUrl: './item-aptitude-processus.component.html',
  providers: [
    provideIcons({
      lucidePlus,
      lucideTrash,
      lucideSearch,
      lucidePen,
      lucideEllipsisVertical,
      lucideArrowUp,
      lucideArrowDown,
      lucideFolderSearch,
    }),
  ],
})
export class ItemAptitudeProcessusComponent {
  readonly #sidepanelService = inject(SidepanelService);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #aptitudeStore = inject(AptitudeStore);
  readonly #competenceStore = inject(CompétenceStore);
  readonly #phaseStore = inject(PhaseStore);

  readonly aptitude = input.required<Aptitude>();
  readonly peutModifier = input.required<boolean>();
  readonly referentiel = input.required<Référentiel>();
  readonly cartographie = input.required<Cartographie>();
  readonly phases = input.required<Phase[]>();

  readonly onCreateIndiceResultat = output<Aptitude | undefined>();
  readonly onCreateProduction = output<Aptitude | undefined>();

  protected readonly productionLabel = computed(
    () => 'P:' + (this.aptitude().productions?.filter((x) => x.idAptitude === this.aptitude().id).length ?? 0),
  );
  protected readonly indiceLabel = computed(
    () => 'I:' + this.aptitude().indicesResultats?.filter((x) => x.idAptitude === this.aptitude().id).length,
  );

  readonly aptitudeActions: ExpansionPanelMenuAction<Aptitude>[] = [
    {
      icon: 'lucidePen',
      label: 'Modifier',
      action: (aptitude: Aptitude | undefined) => this.#modifierAptitude(aptitude),
    },
    {
      icon: 'lucideTrash',
      label: "Supprimer l'aptitude",
      action: (aptitude: Aptitude | undefined) => this.#supprimerAptitude(aptitude),
    },
    {
      icon: 'lucidePlus',
      label: 'Ajouter une production',
      action: (aptitude: Aptitude | undefined) => this.onCreateProduction.emit(aptitude),
    },
    {
      icon: 'lucidePlus',
      label: 'Ajouter un indice de résultats',
      action: (aptitude: Aptitude | undefined) => this.onCreateIndiceResultat.emit(aptitude),
    },
    {
      icon: 'lucideArrowUp',
      label: 'Faire monter',
      action: (aptitude: Aptitude | undefined) => this.#déplacerAptitude(aptitude, true),
    },
    {
      icon: 'lucideArrowDown',
      label: 'Faire descendre',
      action: (aptitude: Aptitude | undefined) => this.#déplacerAptitude(aptitude, false),
    },
  ];

  #supprimerAptitude(aptitude: Aptitude | undefined): void {
    if (!aptitude) return;
    let dialogRef;

    const nbrIndices = aptitude.indicesResultats.length;
    const nbrCompétences = aptitude.competences ? aptitude.competences.length : 0;

    if (nbrIndices > 0) {
      dialogRef = this.#dialog.open(DialogConfirmDeleteComponent, {
        context: {
          nom: aptitude.nom,
          childrenText: `${nbrIndices} indices de résultats, ${aptitude.indicesResultats.flatMap((x) => x.indicateursPerformance).length} indicateurs liés`,
          typeElement: "l'aptitude",
        },
      });
    } else if (nbrCompétences > 0) {
      this.#competenceStore.parAptitude({
        idAptitude: aptitude.id,
        idReferentiel: this.referentiel().id,
        next: () => {
          dialogRef = this.#dialog.open(DialogConfirmDeleteComponent, {
            context: {
              nom: aptitude.nom,
              childrenText: `${nbrCompétences} compétences liées : ${this.#competenceStore.entities().map((x) => x.libelle)}`,
              typeElement: "l'aptitude",
            },
          });
          const toastrId = uuidv7();

          dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
            if (result) {
              this.#aptitudeStore.supprimer({
                idAptitude: aptitude.id,
                next: () => {
                  this.#phaseStore.charger(this.referentiel().id);
                  toast.custom(ToastrSuccessComponent, {
                    componentProps: { message: "Suppression de l'aptitude réussie", id: toastrId },
                    id: toastrId,
                  });
                },
              });
            }
          });
        },
      });
    } else {
      dialogRef = this.#dialog.open(ConfirmDialogComponent, {
        context: {
          titre: `Supprimer l'aptitude`,
          contenu: `Êtes-vous sûr de vouloir supprimer l'aptitude ${aptitude.nom} ?`,
          confirmBtnText: `Confirmer la suppression`,
        },
      });
    }
    const toastrId = uuidv7();

    dialogRef?.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#aptitudeStore.supprimer({
          idAptitude: aptitude.id,
          next: () => {
            this.#phaseStore.charger(this.referentiel().id);
            toast.custom(ToastrSuccessComponent, {
              componentProps: { message: "Suppression de l'aptitude réussie", id: toastrId },
              id: toastrId,
            });
          },
        });
      }
    });
  }

  #modifierAptitude(aptitude: Aptitude | undefined): void {
    if (!aptitude) return;

    this.#sidepanelService.setComponentToDisplay(
      "Modifier l'aptitude",
      FormAptitudeComponent,
      `Modifiez le nom et le parent de votre aptitude. Si besoin de modifier les indices de résultats rattachés, cliquez sur “gérer les indices de résultats” dans le sous-menu de l’aptitude.`,
      {
        aptitude: aptitude,
        referentiel: this.referentiel(),
        cartographie: this.cartographie(),
      },
    );
    this.#sidepanelService.open();
  }

  #déplacerAptitude(aptitude: Aptitude | undefined, isUp: boolean): void {
    if (!aptitude) return;

    const toastrId = uuidv7();

    const data: AptitudeUpdateRequest = {
      id: aptitude.id,
      idReferentiel: aptitude.idReferentiel!,
      nom: aptitude.nom,
      indice: isUp ? aptitude.indice - 1 : aptitude.indice + 1,
      idPhase: aptitude.idPhase,
    };

    this.#aptitudeStore.modifier({
      idAptitude: aptitude.id,
      data,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: { message: 'Réordonnancement des aptitudes réussi', id: toastrId },
          id: toastrId,
        });
        this.#sidepanelService.close();
        this.#phaseStore.charger(this.referentiel().id);
      },
    });
  }
}
